import type { FeatureFlagValue } from '@posthog/core';
import { PostHog } from 'posthog-js';
import { createContext, useContext } from 'react';

enum PostHogFeatureFlagMap {
  EnableIconPicker = 'enable-icon-picker',
  EnableAdditionalExpressOnboardingOptions = 'additional-express-onboarding-options-enabled',
  EnableAiWorkspaceCustomization = 'ai-workspace-customization',
  EnableAlphaApps = 'enable-alpha-apps',
  EnableAppSetup = 'enable_app_setup',
  EnableAppBuilder = 'enable_app_builder',
  EnableBetaApps = 'enable-beta-apps',
  EnableBillingConsolidation = 'billing-consolidation',
  EnableCompanyImport = 'enable_company_import',
  EnableContextBarVisualUpgrades = 'context_bar_visual_upgrades',
  EnableContextBarOtherPage = 'context_bar_other_pages',
  EnableIUAccessControl = 'iu_access_control',
  EnableMicrodepositVerification = 'enable-microdeposit-verification',
  EnableMessageEditorInput = 'message-editor-input',
  EnableOtherMessagesEditor = 'enable-other-messages-editor',
  EnableDuplicateFileName = 'duplicate_file_name',
  EnableFilesAppPerformance = 'files-app-performance',
  EnableLoadFileChannels = 'details-page-file-channels-loading',
  EnableNewEditorFormatter = 'new-editor-formatter',
  EnableInvoiceAddressCustomFields = 'invoice_address_custom_fields',
  EnableAuditLogs = 'audit_logs',
}

export type FlagContextType = Record<
  'EnableManyCompanies' | keyof typeof PostHogFeatureFlagMap,
  boolean
>;

export const DefaultFlagsContext: FlagContextType = {
  // Permanently enabled
  EnableManyCompanies: true,

  // PostHog flags
  EnableAdditionalExpressOnboardingOptions: false,
  EnableAiWorkspaceCustomization: false,
  EnableAlphaApps: false,
  EnableAppBuilder: false,
  EnableAppSetup: false,
  EnableBetaApps: false,
  EnableBillingConsolidation: false,
  EnableCompanyImport: false,
  EnableContextBarOtherPage: false,
  EnableContextBarVisualUpgrades: false,
  EnableDuplicateFileName: false,
  EnableFilesAppPerformance: false,
  EnableIUAccessControl: false,
  EnableLoadFileChannels: false,
  EnableMessageEditorInput: false,
  EnableMicrodepositVerification: false,
  EnableNewEditorFormatter: false,
  EnableOtherMessagesEditor: false,
  EnableInvoiceAddressCustomFields: false,
  EnableAuditLogs: false,
  EnableIconPicker: false,
};

/**
 * These flags are available on the server side, but are not used so we
 * purposefully exclude them
 */
type ClientOnlyFeatureFlagKeysType = 'EnableAppSetup';

export const getServerFlagsContextValue = (
  posthogFlags: Record<PostHogFeatureFlagMap, FeatureFlagValue>,
): Record<
  Exclude<keyof FlagContextType, ClientOnlyFeatureFlagKeysType>,
  boolean
> => ({
  EnableManyCompanies: true,

  // PostHog flags
  EnableIconPicker: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableIconPicker],
  ),
  EnableAdditionalExpressOnboardingOptions: Boolean(
    posthogFlags[
      PostHogFeatureFlagMap.EnableAdditionalExpressOnboardingOptions
    ],
  ),
  EnableAiWorkspaceCustomization: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAiWorkspaceCustomization],
  ),
  EnableAlphaApps: Boolean(posthogFlags[PostHogFeatureFlagMap.EnableAlphaApps]),
  EnableAppBuilder: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableAppBuilder],
  ),
  EnableBetaApps: Boolean(posthogFlags[PostHogFeatureFlagMap.EnableBetaApps]),
  EnableBillingConsolidation: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableBillingConsolidation],
  ),
  EnableCompanyImport: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableCompanyImport],
  ),
  EnableContextBarVisualUpgrades: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableContextBarVisualUpgrades],
  ),
  EnableContextBarOtherPage: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableContextBarOtherPage],
  ),
  EnableIUAccessControl: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableIUAccessControl],
  ),
  EnableMicrodepositVerification: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableMicrodepositVerification],
  ),
  EnableMessageEditorInput: Boolean(posthogFlags['message-editor-input']),
  EnableOtherMessagesEditor: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableOtherMessagesEditor],
  ),
  EnableDuplicateFileName: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableDuplicateFileName],
  ),
  EnableFilesAppPerformance: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableFilesAppPerformance],
  ),
  EnableLoadFileChannels: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableLoadFileChannels],
  ),
  EnableNewEditorFormatter: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableNewEditorFormatter],
  ),
  EnableInvoiceAddressCustomFields: Boolean(
    posthogFlags[PostHogFeatureFlagMap.EnableInvoiceAddressCustomFields],
  ),
  EnableAuditLogs: Boolean(posthogFlags[PostHogFeatureFlagMap.EnableAuditLogs]),
});

export const getClientFlagsContextValue = (
  posthogFlags: PostHog,
): Record<keyof FlagContextType, boolean | undefined> => ({
  EnableManyCompanies: true,

  // PostHog flags
  EnableAdditionalExpressOnboardingOptions: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAdditionalExpressOnboardingOptions,
  ),
  EnableAiWorkspaceCustomization: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAiWorkspaceCustomization,
  ),
  EnableAlphaApps: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAlphaApps,
  ),
  EnableAppSetup: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppSetup,
  ),
  EnableAppBuilder: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAppBuilder,
  ),
  EnableBetaApps: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableBetaApps,
  ),
  EnableBillingConsolidation: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableBillingConsolidation,
  ),
  EnableCompanyImport: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableCompanyImport,
  ),
  EnableContextBarVisualUpgrades: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableContextBarVisualUpgrades,
  ),
  EnableContextBarOtherPage: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableContextBarOtherPage,
  ),
  EnableIUAccessControl: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableIUAccessControl,
  ),
  EnableMicrodepositVerification: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMicrodepositVerification,
  ),
  EnableMessageEditorInput: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableMessageEditorInput,
  ),
  EnableOtherMessagesEditor: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableOtherMessagesEditor,
  ),
  EnableDuplicateFileName: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableDuplicateFileName,
  ),
  EnableFilesAppPerformance: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableFilesAppPerformance,
  ),
  EnableLoadFileChannels: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableLoadFileChannels,
  ),
  EnableNewEditorFormatter: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableNewEditorFormatter,
  ),
  EnableInvoiceAddressCustomFields: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableInvoiceAddressCustomFields,
  ),
  EnableAuditLogs: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableAuditLogs,
  ),
  EnableIconPicker: posthogFlags.isFeatureEnabled(
    PostHogFeatureFlagMap.EnableIconPicker,
  ),
});

export const FlagsContext = createContext(DefaultFlagsContext);

/**
 * Custom hook to access feature flags from the context. This abstracts away the
 * underlying implementation and allows components to easily check if a feature is enabled.
 */
export function useFeatureFlags(): FlagContextType {
  return useContext(FlagsContext);
}
